import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui';
import SEO from '../components/shared/SEO';

/**
 * How It Works Page
 *
 * Comprehensive trust-building page explaining the platform,
 * security measures, and guarantees for both buyers and sellers.
 */
export default function HowItWorks() {
  const { hash } = useLocation();

  // Scroll to hash on mount/hash change
  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [hash]);

  return (
    <div className="bg-midnight">
      <SEO
        title="How It Works"
        description="Learn how Raflr works for buyers and sellers. Secure payments, skill-based entry, local pickup, and buyer protection. Fair, secure, and transparent."
        path="/how-it-works"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-mesh-dark py-16 md:py-24">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display text-cream mb-6">
              Fair, Secure, <span className="text-primary">Transparent</span>
            </h1>
            <p className="text-lg md:text-xl text-cream/70 mb-8">
              Raflr is built on trust. Every transaction is protected, every competition is legitimate,
              and your money is safe until you collect your item.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/browse">
                <Button size="lg">Start Browsing</Button>
              </Link>
              <Link to="/create">
                <Button variant="outline" size="lg">List an Item</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 border-b border-cream/10">
        <div className="page-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: '🔒', title: 'Secure Payments', subtitle: 'Powered by Stripe' },
              { icon: '⚖️', title: 'Skill-Based', subtitle: 'Legitimate competition' },
              { icon: '🛡️', title: 'Buyer Protection', subtitle: '100% refund guarantee' },
              { icon: '✅', title: 'Verified Sellers', subtitle: 'Phone & ID verified' },
            ].map(({ icon, title, subtitle }) => (
              <div key={title}>
                <span className="text-3xl mb-3 block">{icon}</span>
                <h3 className="font-semibold text-cream">{title}</h3>
                <p className="text-sm text-cream/50">{subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Buyers */}
      <section className="py-16 md:py-24">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-primary/20 text-primary rounded-full text-sm font-semibold mb-4">
                For Buyers
              </span>
              <h2 className="text-3xl md:text-4xl font-display text-cream mb-4">
                How Buying Works
              </h2>
              <p className="text-cream/60 max-w-2xl mx-auto">
                Browse items, enter competitions, and collect locally. Your payment is protected every step of the way.
              </p>
            </div>

            <div className="space-y-8">
              {[
                {
                  step: 1,
                  title: 'Browse & Choose',
                  description: 'Explore items listed by verified sellers in your area. See exactly how many entries have been sold and your odds of winning.',
                  details: ['Real photos from real sellers', 'Transparent entry counts', 'Clear pricing - no hidden fees'],
                },
                {
                  step: 2,
                  title: 'Answer & Enter',
                  description: 'Demonstrate your skill by answering a question to enter. This is what makes Raflr a legitimate competition, not gambling.',
                  details: ['Skill-based entry questions', 'Unlimited attempts if you get it wrong', 'Question changes each time for fairness'],
                },
                {
                  step: 3,
                  title: 'Pay Securely',
                  description: 'Your payment is processed by Stripe, the same payment platform used by Amazon and Google. Funds are held securely until the competition ends.',
                  details: ['Payment held in escrow', 'Full refund if competition is cancelled', 'No charge until you submit'],
                },
                {
                  step: 4,
                  title: 'Competition Ends',
                  description: "When the competition ends, a winner is selected from all valid entries. You'll be notified instantly if you've won.",
                  details: ['Winner notified by email & SMS', 'Seller details revealed to winner', 'All entries visible on competition page'],
                },
                {
                  step: 5,
                  title: 'Collect Your Item',
                  description: "If you win, you'll receive the seller's location and can arrange pickup. Both parties confirm the handover before funds are released.",
                  details: ['In-app messaging with seller', 'Confirm receipt before seller is paid', 'Dispute resolution if issues arise'],
                },
              ].map(({ step, title, description, details }) => (
                <div key={step} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary/20 border border-primary/40 rounded-xl flex items-center justify-center">
                      <span className="text-primary font-bold text-lg">{step}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-cream mb-2">{title}</h3>
                    <p className="text-cream/70 mb-3">{description}</p>
                    <ul className="space-y-1">
                      {details.map((detail) => (
                        <li key={detail} className="flex items-center gap-2 text-sm text-cream/50">
                          <svg className="w-4 h-4 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="page-container">
        <div className="border-t border-cream/10" />
      </div>

      {/* How It Works - Sellers */}
      <section className="py-16 md:py-24">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-accent/20 text-accent rounded-full text-sm font-semibold mb-4">
                For Sellers
              </span>
              <h2 className="text-3xl md:text-4xl font-display text-cream mb-4">
                How Selling Works
              </h2>
              <p className="text-cream/60 max-w-2xl mx-auto">
                List your item, set your price, and get paid when it's collected. We handle payments and protect you from no-shows.
              </p>
            </div>

            <div className="space-y-8">
              {[
                {
                  step: 1,
                  title: 'List Your Item',
                  description: 'Upload photos, write a description, and set your entry price and quantity. Our AI can help suggest fair pricing.',
                  details: ['Up to 8 photos per listing', 'AI-powered pricing suggestions', 'Set your own minimum threshold'],
                },
                {
                  step: 2,
                  title: 'Set Up Payments',
                  description: "Connect your bank account through Stripe. It takes 2 minutes and ensures you get paid directly to your account.",
                  details: ['One-time setup via Stripe Connect', 'Direct deposit to your bank', 'No waiting for cheques'],
                },
                {
                  step: 3,
                  title: 'List for Free',
                  description: "Your competition goes live immediately. We only take 5% when you succeed - this covers payment processing, escrow, and dispute handling.",
                  details: ['Free to list', '5% fee only on successful sales', 'You keep 95% of entry revenue'],
                },
                {
                  step: 4,
                  title: 'Watch Entries Come In',
                  description: "Share your competition and watch entries roll in. You'll see real-time progress toward your minimum threshold.",
                  details: ['Real-time entry tracking', 'Shareable competition link', 'Notifications when you hit milestones'],
                },
                {
                  step: 5,
                  title: 'Complete the Handover',
                  description: "When the competition ends and a winner is drawn, arrange pickup. Once both parties confirm handover, funds are released to you.",
                  details: ['Winner contacts you to arrange pickup', 'Both parties confirm in-app', 'Funds released within 2 business days'],
                },
              ].map(({ step, title, description, details }) => (
                <div key={step} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-accent/20 border border-accent/40 rounded-xl flex items-center justify-center">
                      <span className="text-accent font-bold text-lg">{step}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-cream mb-2">{title}</h3>
                    <p className="text-cream/70 mb-3">{description}</p>
                    <ul className="space-y-1">
                      {details.map((detail) => (
                        <li key={detail} className="flex items-center gap-2 text-sm text-cream/50">
                          <svg className="w-4 h-4 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Payment Security Section */}
      <section className="py-16 md:py-24 bg-midnight-light">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display text-cream mb-4">
                Your Money is <span className="text-primary">Protected</span>
              </h2>
              <p className="text-cream/60 max-w-2xl mx-auto">
                We never hold your money. All payments are processed and secured by Stripe,
                the world's leading payment platform trusted by millions of businesses.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* How Escrow Works */}
              <div className="bg-midnight border border-midnight-lighter rounded-xl p-6">
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-cream mb-3">How Payment Protection Works</h3>
                <ol className="space-y-3 text-cream/70">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-bold text-primary">1</span>
                    <span>You pay when entering a competition</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-bold text-primary">2</span>
                    <span>Funds are held securely by Stripe (not Raflr)</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-bold text-primary">3</span>
                    <span>Winner collects item and confirms receipt</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-bold text-primary">4</span>
                    <span>Only then is the seller paid</span>
                  </li>
                </ol>
              </div>

              {/* Refund Guarantees */}
              <div className="bg-midnight border border-midnight-lighter rounded-xl p-6">
                <div className="w-12 h-12 bg-success/20 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-cream mb-3">When You Get a Full Refund</h3>
                <ul className="space-y-3 text-cream/70">
                  <li className="flex gap-3">
                    <svg className="w-5 h-5 text-success flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Competition doesn't meet minimum entries</span>
                  </li>
                  <li className="flex gap-3">
                    <svg className="w-5 h-5 text-success flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Seller cancels the competition</span>
                  </li>
                  <li className="flex gap-3">
                    <svg className="w-5 h-5 text-success flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Item not as described (dispute resolved in your favour)</span>
                  </li>
                  <li className="flex gap-3">
                    <svg className="w-5 h-5 text-success flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Seller doesn't hand over the item</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Stripe Badge */}
            <div className="mt-8 text-center">
              <p className="text-cream/50 text-sm mb-3">Payments secured by</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-midnight border border-cream/10 rounded-lg">
                <svg className="h-8 w-auto" viewBox="0 0 468 222.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#635BFF" d="M414 113.4c0-25.6-12.4-45.8-36.1-45.8-23.8 0-38.2 20.2-38.2 45.6 0 30.1 17 45.3 41.4 45.3 11.9 0 20.9-2.7 27.7-6.5v-20c-6.8 3.4-14.6 5.5-24.5 5.5-9.7 0-18.3-3.4-19.4-15.2h48.9c0-1.3.2-6.5.2-8.9zm-49.4-9.5c0-11.3 6.9-16 13.2-16 6.1 0 12.6 4.7 12.6 16h-25.8zM301.1 67.6c-9.8 0-16.1 4.6-19.6 7.8l-1.3-6.2h-22v116.6l25-5.3.1-28.3c3.6 2.6 8.9 6.3 17.7 6.3 17.9 0 34.2-14.4 34.2-46.1-.1-29-16.6-44.8-34.1-44.8zm-6 68.9c-5.9 0-9.4-2.1-11.8-4.7l-.1-37.1c2.6-2.9 6.2-4.9 11.9-4.9 9.1 0 15.4 10.2 15.4 23.3 0 13.4-6.2 23.4-15.4 23.4zM223.8 61.7l25.1-5.4V36l-25.1 5.3zM223.8 69.3h25.1v87.5h-25.1zM196.9 76.7l-1.6-7.4h-21.6v87.5h25V97.5c5.9-7.7 15.9-6.3 19-5.2v-23c-3.2-1.2-14.9-3.4-20.8 7.4zM146.9 47.6l-24.4 5.2-.1 80.1c0 14.8 11.1 25.7 25.9 25.7 8.2 0 14.2-1.5 17.5-3.3V135c-3.2 1.3-19 5.9-19-8.9V90.6h19V69.3h-19l.1-21.7zM79.3 94.7c0-3.9 3.2-5.4 8.5-5.4 7.6 0 17.2 2.3 24.8 6.4V72.2c-8.3-3.3-16.5-4.6-24.8-4.6C67.5 67.6 54 78.2 54 95.9c0 27.6 38 23.2 38 35.1 0 4.6-4 6.1-9.6 6.1-8.3 0-18.9-3.4-27.3-8v23.8c9.3 4 18.7 5.7 27.3 5.7 20.8 0 35.1-10.3 35.1-28.2-.1-29.8-38.2-24.5-38.2-35.7z"/>
                </svg>
                <span className="text-cream/70 text-sm">Secure Payments</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 bg-midnight-light scroll-mt-20">
        <div className="page-container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display text-cream mb-4">
                Common Questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  q: "Is Raflr legal?",
                  a: "Yes. Raflr operates as a prize competition, not gambling. The skill-based entry question means it's classified as a competition of skill under Irish and UK law. We comply with all relevant consumer protection regulations."
                },
                {
                  q: "What happens if the competition doesn't reach its minimum?",
                  a: "You get a full refund, automatically. Sellers set a minimum threshold (e.g., 80% of entries). If that's not met by the end date, all entrants are refunded in full within 5-7 business days."
                },
                {
                  q: "How is the winner selected?",
                  a: "When the competition ends, a winner is selected automatically from all valid entries. The selection happens instantly and the winner is notified by email and SMS."
                },
                {
                  q: "What if the item isn't as described?",
                  a: "Funds aren't released to the seller until you confirm receipt. If the item doesn't match the listing, raise a dispute within 48 hours. We'll review the evidence and issue a full refund if your claim is valid."
                },
                {
                  q: "Can I cancel my entry?",
                  a: "Entries cannot be cancelled once submitted, as the competition entry count is public information that others rely on. However, you'll get a full refund if the competition is cancelled or doesn't meet its minimum."
                },
                {
                  q: "How do sellers get paid?",
                  a: "Sellers connect their bank account via Stripe. When the winner confirms they've collected the item, funds are released and typically arrive within 2 business days."
                },
                {
                  q: "What's the skill question for?",
                  a: "The skill question ensures Raflr is a prize competition, not a lottery. Entry requires demonstrating skill, which is what keeps Raflr operating as a legitimate competition under Irish law."
                },
                {
                  q: "Is my payment information safe?",
                  a: "Absolutely. We never see or store your card details. All payments are handled by Stripe, a PCI-DSS Level 1 certified payment processor trusted by Amazon, Google, and millions of businesses worldwide."
                },
              ].map(({ q, a }) => (
                <details
                  key={q}
                  className="group bg-midnight border border-midnight-lighter rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="font-semibold text-cream pr-4">{q}</h3>
                    <svg
                      className="w-5 h-5 text-cream/50 flex-shrink-0 transition-transform group-open:rotate-180"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="px-5 pb-5 text-cream/70">
                    {a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
